import axios from "axios";
import errorHandling from './requestHandling';

// Falls back to the page's own origin when SERVER_URL isn't baked in at build
// time (deployed-dev ships it empty so one build serves both dev hosts on the
// shared dev DB). Local split-port dev and prod keep explicit env values.
const apiUrl = process.env.SERVER_URL || window.location.origin;

const service = axios.create({
    baseURL: apiUrl + "/api",
});

// Safely obtain the single Redux store instance that the app bootstraps in main.tsx
// main.tsx assigns (globalThis as any).__APP_STORE__ = store
function getAppStore(): any | undefined {
    try {
        const g: any = (globalThis as any);
        return g && g.__APP_STORE__ ? g.__APP_STORE__ : undefined;
    } catch {
        return undefined;
    }
}

// Centralized, robust token resolver. Never throws; returns string | null.
export function getAccessToken(): string | null {
    // 1) Try Redux store first (single instance exposed on globalThis)
    try {
        const store = getAppStore();
        const token = store?.getState?.()?.user?.userAuthInfo?.access_token;
        if (typeof token === 'string' && token.length > 0) return token;
    } catch {
        // ignore
    }

    // 2) Try OIDC storage entries (created by oidc-client/oidc libraries)
    try {
        const keys = Object.keys(localStorage).filter(key =>
            key.startsWith('oidc.user:') ||
            (key.startsWith('oidc.') && (localStorage.getItem(key) ?? '').includes('access_token'))
        );
        for (const key of keys) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            try {
                const parsed = JSON.parse(raw);
                const token = parsed?.access_token;
                if (typeof token === 'string' && token.length > 0) return token;
            } catch {
                // skip invalid JSON
            }
        }
    } catch {
        // ignore
    }

    // 3) Try persisted Redux (redux-persist) root slice
    try {
        const persisted = localStorage.getItem('persist:root');
        if (persisted) {
            const root = JSON.parse(persisted);
            if (root && typeof root.user === 'string') {
                try {
                    const userState = JSON.parse(root.user);
                    const token = userState?.userAuthInfo?.access_token;
                    if (typeof token === 'string' && token.length > 0) return token;
                } catch {
                    // ignore parse errors
                }
            }
        }
    } catch {
        // ignore
    }

    return null;
}

service.interceptors.response.use(
    (response) => {
        if (response.data instanceof Blob) {
            return response;
        }
        return response;
    },
    async (error) => errorHandling(error)
);

service.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["timezoneOffset"] = new Date().getTimezoneOffset();
        return config;
    },
    (error) => Promise.reject(error)
);

export default service;